import React, { useEffect, useRef, useState } from 'react';
import { MAP_THEME_LIST } from '../../config/themes';

/**
 * Top-bar switcher for the map theme (look and feel of the canvas).
 *
 * The active theme is shown to everyone; only the map owner can change it
 * (UI-level enforcement — the option list is disabled for collaborators).
 */
const ThemeMenu = ({ mapTheme, isDarkMode }) => {
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!mapTheme) return null;

  const { activeThemeId, canChangeTheme, setTheme } = mapTheme;

  const buttonStyle = {
    width: 28,
    height: 28,
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    border: 0,
    borderRadius: '50%',
    background: isDarkMode ? '#111827' : '#e5e7eb',
    color: isDarkMode ? '#f9fafb' : '#111827',
    cursor: 'pointer',
  };

  const menuStyle = {
    position: 'absolute',
    top: 40,
    right: 0,
    minWidth: 190,
    padding: 8,
    borderRadius: 8,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    zIndex: 210,
  };

  const itemBaseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    border: 0,
    background: 'transparent',
    textAlign: 'left',
    fontSize: 13,
    padding: '6px 8px',
    borderRadius: 6,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: isDarkMode ? '#e5e7eb' : '#111827',
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label="Map theme"
        aria-expanded={open}
        title={canChangeTheme ? 'Map theme' : 'Map theme (only the map owner can change it)'}
        style={buttonStyle}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        <svg
          aria-hidden="true"
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Painter's palette */}
          <path d="M12 3a9 9 0 1 0 0 18h1.5a2.5 2.5 0 0 0 0-5H12a2 2 0 0 1 0-4h6.5A2.5 2.5 0 0 0 21 9.5 8.7 8.7 0 0 0 12 3Z" />
          <circle cx="7.5" cy="10.5" r="0.6" fill="currentColor" />
          <circle cx="10.5" cy="7" r="0.6" fill="currentColor" />
          <circle cx="15" cy="6.5" r="0.6" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Map themes"
          style={menuStyle}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              opacity: 0.65,
              padding: '2px 8px 6px',
              fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              color: isDarkMode ? '#e5e7eb' : '#111827',
            }}
          >
            Map theme
          </div>
          {MAP_THEME_LIST.map((themeOption) => {
            const isActive = themeOption.id === activeThemeId;
            const disabled = !canChangeTheme && !isActive;
            return (
              <button
                key={themeOption.id}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                disabled={!canChangeTheme}
                style={{
                  ...itemBaseStyle,
                  cursor: canChangeTheme ? 'pointer' : 'default',
                  opacity: disabled ? 0.45 : 1,
                  fontWeight: isActive ? 700 : 500,
                }}
                onMouseEnter={(event) => {
                  if (!canChangeTheme) return;
                  event.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => {
                  if (!canChangeTheme) return;
                  if (!isActive && typeof setTheme === 'function') {
                    setTheme(themeOption.id);
                  }
                  setOpen(false);
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 34,
                    height: 22,
                    flexShrink: 0,
                    borderRadius: 5,
                    background: themeOption.preview.background,
                    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 8,
                      borderRadius: 4,
                      background: themeOption.preview.node,
                      border: `1px solid ${themeOption.preview.border}`,
                    }}
                  />
                </span>
                <span style={{ flex: 1 }}>{themeOption.name}</span>
                {isActive && <span aria-hidden="true">✓</span>}
              </button>
            );
          })}
          {!canChangeTheme && (
            <div
              style={{
                fontSize: 11,
                opacity: 0.65,
                padding: '6px 8px 2px',
                fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: isDarkMode ? '#e5e7eb' : '#111827',
              }}
            >
              Only the map owner can change the theme.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThemeMenu;
